import React from 'react';
import styles from './info.module.css';
import { GiftInfo } from './GiftInfo';
import { useViewModel } from './useViewModel';
import { useLocale } from '../../../../context/LocaleContext';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { getInsertsAndCardsProduct } from '../../../../utilities/visualiserVariants.js';

export function Info() {
  const {
    priceModalOpen,
    personalisationChoices,
    editChoice,
    handleClose,
    totalPrice,
    getTitle,
    activeProduct,
    wrapOptions = []
  } = useViewModel();
  const { formatPrice } = useLocale();
  const { visualiserSelection } = useUserInput();

  if (!priceModalOpen) return null;

  // Helper function to create a map of all variants from wrap options
  const createVariantMap = () => {
    const allVariants = new Map();
    wrapOptions.forEach((option) => {
      if (option.variants && Array.isArray(option.variants)) {
        option.variants.forEach((variant) => {
          allVariants.set(variant.id, variant);
        });
      }
      if (option.product_id && option.price !== undefined) {
        allVariants.set(option.product_id, option);
      }
    });
    return allVariants;
  };

  // Helper function to calculate additional costs for a personalisation choice
  const calculateAdditionalCosts = (choice) => {
    const additionalCosts = /** @type {Array<{label: string, amount: number}>} */ ([]);
    const allVariants = createVariantMap();

    // Personalization text cost (for display only - actual calculation is in GiftWrapContext)
    if (choice.text && choice.text.trim() !== '') {
      const personalizationVariant = allVariants.get(choice.vId_gift);
      if (personalizationVariant && personalizationVariant.title?.toLowerCase().includes('custom text')) {
        additionalCosts.push({
          label: 'Personalisation',
          amount: 5
        });
      }
    }

    // Wax stamp cost (for display transparency - also included in main price line)
    if (choice.vId_stamp) {
      const variant = allVariants.get(choice.vId_stamp);
      const stampCost = variant && variant.price ? variant.price : 1;
      additionalCosts.push({
        label: 'Wax Stamp',
        amount: stampCost
      });
    }

    return additionalCosts;
  };

  // Helper function to get the correct variant price for a choice
  const getVariantPrice = (choice) => {
    const allVariants = createVariantMap();

    // Get the variant price if available, otherwise fall back to choice.price
    if (choice.vId_gift) {
      const variant = allVariants.get(choice.vId_gift);
      if (variant && variant.price) {
        return variant.price;
      }
    }

    // Fall back to the stored choice price
    return Number(choice.price) || 0;
  };

  /**
   * Computes the displayed total based on the line items rendered in this modal.
   * This keeps the "Current Total" in sync with what the user sees (base item + insert/card + gift wrap choices).
   * @returns {number} The total in currency units (e.g. 85.98).
   */
  const getDisplayedTotal = () => {
    let total = 0;

    // Base item (only if it is rendered).
    if (activeProduct && activeProduct.title && activeProduct.price > 0) {
      total += Number(activeProduct.price) || 0;
    }

    // Inserts / cards (only if we have a selected variant with a numeric price in cents).
    if (visualiserSelection?.productKey) {
      const variant = visualiserSelection.variant;
      const variantPriceCents = typeof variant?.price === 'number' ? Number(variant.price) : null;
      if (variantPriceCents != null && !Number.isNaN(variantPriceCents)) {
        total += variantPriceCents / 100;
      }
    }

    // Gift wrap choices shown in the list (base + wax stamp).
    personalisationChoices.forEach((choice) => {
      const variantPrice = getVariantPrice(choice);

      let waxStampCost = 0;
      if (choice.vId_stamp) {
        const allVariants = createVariantMap();
        const variant = allVariants.get(choice.vId_stamp);
        waxStampCost = variant && variant.price ? variant.price : 1;
      }

      total += (Number(variantPrice) || 0) + (Number(waxStampCost) || 0);
    });

    return total;
  };

  return (
    <div className={styles['info-modal']}>
      <div
        className={styles['info-modal__overlay']}
        onClick={handleClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            handleClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />
      <div className={styles['info-modal__content']}>
        <div className={styles['info-modal__header']}>
          <h2>Luxury Gift Wrap</h2>
          <button className={styles['info-modal__close']} onClick={handleClose}>
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="icon icon-chev"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.21726 6.52756C8.37346 6.37135 8.62673 6.37135 8.78294 6.52756L12.7829 10.5276C12.9391 10.6838 12.9391 10.937 12.7829 11.0932C12.6267 11.2495 12.3735 11.2495 12.2173 11.0932L8.5001 7.37609L4.78294 11.0932C4.62673 11.2495 4.37346 11.2495 4.21725 11.0932C4.06105 10.937 4.06105 10.6838 4.21725 10.5276L8.21726 6.52756Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
        <div className={styles['info-modal__items']}>
          {/* Show the main product if it has valid values */}
          {activeProduct && activeProduct.title && activeProduct.price > 0 && (
            <GiftInfo
              key="main-product"
              type="Item to be wrapped"
              title={activeProduct.title}
              text={null}
              stamp_label={null}
              price={activeProduct.price}
              onEdit={null}
              additionalCosts={[]}
            />
          )}

          {/* Show the inserts/cards visualiser product while on a visualiser page. */}
          {visualiserSelection?.productKey ? (() => {
            const productKey = visualiserSelection.productKey;
            const product = getInsertsAndCardsProduct({ productKey });
            const variant = visualiserSelection.variant;

            const type =
              productKey === 'inserts' || productKey === 'themedInserts'
                ? 'Box insert'
                : 'Greetings card';

            const productTitle = product?.title || 'Personal touch';
            const variantTitle =
              typeof variant?.public_title === 'string'
                ? variant.public_title
                : typeof variant?.title === 'string'
                  ? variant.title
                  : null;

            const displayVariantTitle = variantTitle ? `${variantTitle}`.replace('Image Upload / ', '') : 'Please choose a design';
            const price = typeof variant?.price === 'number' ? Number(variant.price) / 100 : null;

            return (
              <GiftInfo
                key={`visualiser-${productKey}`}
                type={type}
                title={productTitle}
                text={displayVariantTitle}
                stamp_label={null}
                price={price}
                onEdit={null}
                additionalCosts={[]}
              />
            );
          })() : null}

          {personalisationChoices.map((choice, index) => {
            let type = 'Gift Card';
            if (choice.type === 'bag') {
              type = 'Gift Packaging';
            } else if (choice.type === 'box') {
              type = 'Gift Box';
            }

            const additionalCosts = calculateAdditionalCosts(choice);
            const variantPrice = getVariantPrice(choice);

            // Calculate wax stamp cost to include in main price
            let waxStampCost = 0;
            if (choice.vId_stamp) {
              const allVariants = createVariantMap();
              const variant = allVariants.get(choice.vId_stamp);
              waxStampCost = variant && variant.price ? variant.price : 1;
            }

            // Main price includes base variant price + wax stamp cost
            const mainPrice = variantPrice + waxStampCost;

            return (
              <GiftInfo
                key={choice.parentId}
                type={type}
                title={getTitle(choice)}
                text={choice.text}
                stamp_label={choice.vId_stamp_label}
                price={mainPrice}
                onEdit={() => editChoice(index)}
                additionalCosts={/** @type {Array<{label: string, amount: number}>} */ (additionalCosts)}
              />
            );
          })}
        </div>
        <div className={styles['info-modal__total']}>
          <span>Current Total:</span>
          <span>{formatPrice({ amount: getDisplayedTotal() })}</span>
        </div>
        <button className="button button--secondary button--black button--full-width" onClick={handleClose}>
          Close
        </button>
      </div>
    </div>
  );
}
